import {RepositoryListItem} from '@github-ui/copilot-chat/components/RepositoryListItem'
import {useRepositoryItems} from '@github-ui/copilot-chat/hooks/use-repository-items'
import {copilotLocalStorage} from '@github-ui/copilot-chat/utils/copilot-local-storage'
import {useChatState} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {useChatManager} from '@github-ui/copilot-chat/utils/CopilotChatManagerContext'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {RepositoryPickerTopRepositories$key} from '@github-ui/item-picker/RepositoryPickerTopRepositories.graphql'
import {SingleSignOnBanner} from '@github-ui/single-sign-on-banner'
import {testIdProps} from '@github-ui/test-id-props'
import {ActionList} from '@primer/react'
import {SelectPanel} from '@primer/react/experimental'
import {clsx} from 'clsx'
import {type RefObject, useCallback, useEffect, useMemo, useState} from 'react'
import {flushSync} from 'react-dom'

import {isRepository} from '../utils/copilot-chat-helpers'
import {COPILOT_CHAT_TOPIC_PICKER_PORTAL_ROOT, TopicPickerPortalContainer} from './PortalContainerUtils'

interface RepoSelectPanelProps {
  open: boolean
  onOpenChange: (value: boolean) => void
  submitReturnFocusRef: RefObject<HTMLElement>
  cancelReturnFocusRef: RefObject<HTMLElement>
}

export function RepoSelectPanel({
  open,
  onOpenChange,
  submitReturnFocusRef,
  cancelReturnFocusRef,
}: RepoSelectPanelProps) {
  const {currentRepository, selectedThreadID, ssoOrganizations, currentTopic} = useChatState()
  const manager = useChatManager()

  const [isFirstTimeLoading, setIsFirstTimeLoading] = useState(true)
  const [filter, setFilter] = useState('')

  useEffect(() => {
    if (!open) setFilter('')
  }, [open])

  const [topRepositoryResults, setTopRepositoryResults] = useState<RepositoryPickerTopRepositories$key | undefined>(
    undefined,
  )

  const onSelect = useCallback(
    async (repoId: number | string) => {
      copilotLocalStorage.setSelectedTopic(selectedThreadID ?? '', repoId.toString())
      manager.clearCurrentReferences(['image', 'issue'])
      await manager.fetchCurrentRepo(repoId)
    },
    [manager, selectedThreadID],
  )

  const {repositories, loading: reposLoading} = useRepositoryItems(
    filter,
    topRepositoryResults,
    setTopRepositoryResults,
    open,
    currentRepository,
  )

  const srRepoSearchStatus = useMemo(() => {
    if (reposLoading) return 'Loading repositories'
    if (repositories.length === 0) return 'No repositories found'
    if (repositories.length === 1) return '1 repository found'
    return `${repositories.length} repositories found`
  }, [reposLoading, repositories.length])

  const onSearchInputChange: React.ChangeEventHandler<HTMLInputElement> = event => {
    setFilter(event.currentTarget.value)
  }

  useEffect(() => {
    if (repositories.length > 0 && isFirstTimeLoading) {
      setIsFirstTimeLoading(false)
    }
  }, [repositories.length, isFirstTimeLoading])

  const ssoOrgNames = useMemo(() => {
    if (!ssoOrganizations) return []
    return ssoOrganizations.map(org => org.login)
  }, [ssoOrganizations])

  const selectedNwo = isRepository(currentTopic) ? `${currentTopic.ownerLogin}/${currentTopic.name}` : null

  // shift repositories so selected repo is always at the top
  if (selectedNwo) {
    const selectedIndex = repositories.findIndex(repo => repo.nwo === selectedNwo)
    if (selectedIndex > 0) {
      const [selectedRepo] = repositories.splice(selectedIndex, 1)

      if (selectedRepo) {
        repositories.splice(0, 0, selectedRepo)
      }
    }
  }

  return (
    <div {...testIdProps('repo-select-panel')}>
      {/* eslint-disable-next-line primer-react/no-system-props */}
      <SelectPanel
        width="large"
        maxHeight="xlarge"
        onCancel={() => {
          flushSync(() => onOpenChange(false))
          cancelReturnFocusRef.current?.focus()
          sendEvent('dotcom_chat.activate', {target: 'REPOSITORY_DIALOG_CLOSE', mode: 'immersive'})
        }}
        onSubmit={() => {
          flushSync(() => onOpenChange(false))
          submitReturnFocusRef.current?.focus()
        }}
        open={open}
        variant="modal"
        selectionVariant="instant"
        title="Attach a repository"
      >
        <SelectPanel.Header className={clsx(ssoOrgNames.length > 0 && 'border-bottom-0')}>
          <SelectPanel.SearchInput
            loading={reposLoading && !isFirstTimeLoading}
            onChange={onSearchInputChange}
            placeholder="Search repositories"
          />
        </SelectPanel.Header>
        <SingleSignOnBanner
          portalContainerName={COPILOT_CHAT_TOPIC_PICKER_PORTAL_ROOT}
          protectedOrgs={ssoOrgNames}
          redirectURI={() => `/search/refresh_blackbird_caches?return_to=${location.href}`}
          isDisplayedInSelectPanel
        />
        <TopicPickerPortalContainer />
        <ActionList sx={{minHeight: 340}}>
          <div role="status" className="sr-only">
            {srRepoSearchStatus}
          </div>
          {reposLoading && isFirstTimeLoading ? (
            <SelectPanel.Loading>Fetching repositories&hellip;</SelectPanel.Loading>
          ) : repositories.length === 0 && !reposLoading ? (
            <SelectPanel.Message variant="empty" title="No repositories found">
              Try a different search term
            </SelectPanel.Message>
          ) : (
            repositories.map(repo => (
              <RepositoryListItem key={repo.nwo} repo={repo} onSelect={onSelect} selected={repo.nwo === selectedNwo} />
            ))
          )}
        </ActionList>
      </SelectPanel>
    </div>
  )
}

try{ RepoSelectPanel.displayName ||= 'RepoSelectPanel' } catch {}